import React from 'react';
import { useDebounce } from '@famly/stat_ts-utils_debounce';
/**
 * Properly debounces a controlled input field.
 *
 * This works by keeping a local reference for the latest valid value entered by the user and
 * only propagating it, whenever necessary.
 *
 * Important!
 *
 * You need to make sure that the debounced value gets properly propagated in case the user
 * hits "enter", which by default submits a form!
 * You can catch such a submit event by listening to the `onKeyDown` event and check for
 * `event.key === "Enter"`
 *
 * @param value the current value for the controlled input. This will rerender the hook and
 *   replace the current, "local" value with what was passed in
 * @param onChange the un-debounced onChange function, that you would usually just pass to the
 *   controlled input field
 * @returns onChange and onBlur handlers to be attached to the controlled input
 */
export function useDebounceControlledInputChange(value, onChange) {
    const [localValue, setLocalValue] = React.useState(value);
    const changePropagatedRef = React.useRef(false);
    const debouncedChange = useDebounce((newValue) => {
        if (!changePropagatedRef.current) {
            changePropagatedRef.current = true;
            onChange === null || onChange === void 0 ? void 0 : onChange(newValue);
        }
    }, [onChange]);
    React.useEffect(() => {
        setLocalValue(value);
    }, [value]);
    const handleBlur = React.useCallback(() => {
        if (!changePropagatedRef.current) {
            changePropagatedRef.current = true;
            onChange === null || onChange === void 0 ? void 0 : onChange(localValue);
        }
    }, [onChange, localValue]);
    const handleChange = React.useCallback((newValue) => {
        changePropagatedRef.current = false;
        setLocalValue(newValue);
        debouncedChange(newValue);
    }, [debouncedChange]);
    return { value: localValue, handleChange, handleBlur };
}
